import { MilestoneMap1, Objective1 } from '../../../types';
import { DependencyRecord } from '@mm-shared/dependencies';
import { DataNode } from '../types';
import { buildObjectiveHierarchy } from '../utils/objectiveHierarchyBuilder';
import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';
import { filterItemsForInterval, getLatestStatusInInterval } from '@mm-shared/intervals';
import { buildDependencyDataNodes } from '../utils/dependencyBuilder';

/**
 * Build Objectives data node structure
 *
 * Structure: Milestone Map → Major Objective → Objective → Status Report Date → Status
 * This extracts and reuses the hierarchy logic from the existing system.
 */
export function buildObjectivesDataNode(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<MilestoneMap1> {
  // Create the root milestone map node
  const rootNode: DataNode<MilestoneMap1> = {
    id: milestoneMap.uuid,
    data: milestoneMap,
    children: [],
    type: 'MilestoneMap',
    metadata: {
      defaultExpanded: true,
    },
  };

  // Build the objective hierarchy using shared utility
  const hierarchyItems = buildObjectiveHierarchy(milestoneMap, objectives, (objective, map) =>
    createObjectiveDataNode(objective, map, dependencyRecords, allMaps, objectives),
  );

  rootNode.children = hierarchyItems;

  return rootNode;
}

/**
 * Create an objective data node with its status report date children
 */
function createObjectiveDataNode(
  objective: Objective1,
  milestoneMap: MilestoneMap1,
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
  allObjectives: Objective1[] = [],
): DataNode<Objective1> {
  const statusReportDateChildren = createStatusReportDateDataNodes(
    objective,
    milestoneMap,
    dependencyRecords,
    allMaps,
    allObjectives,
  );

  return {
    id: objective.uuid,
    data: objective,
    children: statusReportDateChildren,
    type: 'Objective',
  };
}

/**
 * Create status report date data nodes for an objective
 *
 * Level 3: Creates StatusReportDate nodes that contain Status, Results, Comments as children
 */
function createStatusReportDateDataNodes(
  objective: Objective1,
  milestoneMap: MilestoneMap1,
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
  allObjectives: Objective1[] = [],
): DataNode<any>[] {
  const intervals = getIntervalsForMilestoneMap(milestoneMap);

  return intervals.map((interval) => {
    // Find status for this interval using timezone-agnostic interval matching
    const status = getLatestStatusInInterval(objective.statuses, interval);

    const statusReportDateLabel = `${interval.end.toLocaleDateString()} - ${status?.statusCode || 'No status'}`;

    // Level 4: Create Status nodes as children of StatusReportDate
    const statusChildren = createStatusDataNodeForInterval(
      objective,
      status,
      interval,
      dependencyRecords,
      allMaps,
      allObjectives,
    );

    return {
      id: `${objective.uuid}-statusreportdate-${interval.end.toISOString()}`,
      data: {
        interval,
        status,
        statusDate: interval.end.toISOString().split('T')[0],
        statusLabel: statusReportDateLabel,
      },
      children: statusChildren,
      type: 'StatusReportDate',
      metadata: {
        statusUuid: status?.uuid,
        objectiveUuid: objective.uuid,
        forIntervalEnding: interval.end.toISOString().split('T')[0],
      },
    };
  });
}

/**
 * Create Status, Results, and Comments nodes for a specific interval (Level 4)
 *
 * This creates the actual Level 4 nodes that will use our label generators
 */
function createStatusDataNodeForInterval(
  objective: Objective1,
  status: any,
  interval: { start: Date; end: Date },
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
  allObjectives: Objective1[] = [],
): DataNode<any>[] {
  const level4Nodes: DataNode<any>[] = [];

  // Status node - only create if there's actually a status
  if (status && status.statusCode) {
    const statusLabel = `${objective.text} - ${status.statusCode}`;
    level4Nodes.push({
      id: `${objective.uuid}-status-${interval.end.toISOString()}`,
      data: {
        objective, // Include the full objective object for the label generator
        status, // The status object for this interval
        statusLabel, // Pre-formatted label
        interval, // The time interval
      },
      children: [],
      type: 'Status', // This will use our Status label generator
      metadata: {
        statusUuid: status?.uuid,
        objectiveUuid: objective.uuid,
        forIntervalEnding: interval.end.toISOString().split('T')[0],
      },
    });
  }

  // Results nodes - add all results due in this interval
  const resultsForInterval = filterItemsForInterval(
    objective.results || [],
    interval.start,
    interval.end,
  );

  resultsForInterval.forEach((result) => {
    // Build dependency children for this result
    const dependencyChildren = buildDependencyDataNodes(
      result,
      dependencyRecords,
      allMaps,
      allObjectives,
    );

    level4Nodes.push({
      id: `${result.uuid}-interval-${interval.end.toISOString()}`,
      data: {
        result,
        objective,
        interval,
      },
      children: dependencyChildren,
      type: result.isKeyResult ? 'KeyResult' : 'Result',
      metadata: {
        objectiveUuid: objective.uuid,
        forIntervalEnding: interval.end.toISOString().split('T')[0],
      },
    });
  });

  // TODO: Add Comments nodes when needed
  // Comments would follow the same pattern as Results

  return level4Nodes;
}
